import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import {
  ApiCreateDocumentGenerationJob,
  ApiService,
  CompanyDocuments,
  CompanyService,
  RetrieveDocumentGenerationJob,
} from '../../../../../api';
import { useStore } from '../../../../../common/store';
import { UseStoreValues } from '../../../../../common/types/store';
import { buildEffortPayload, EffortSelector } from '../../../../../components/EffortSelector';
import type { EffortSelection } from '../../../../../components/EffortSelector';
import { EventType, useEventBus } from '../../../../../context';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { useDocuments } from '../../../../../services/hooks';
import { useGetCaptableInfo } from '../../../../../services/hooks/captable';
import { extractResponseErrorMessage, Logger } from '../../../../../utilities';
import { AIGradientButton } from '../../../../DocumentGenerationJobs/components/AIGradient';

interface SelectedFile {
  file_id: number;
  label: string;
}

const GenerateCapTableJob = () => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { captableInfo, companyInfo, firmInfo } = storeValue;
  const [, getCapTableInfo] = useGetCaptableInfo();
  const { addJob, removeJob } = useAsyncProcessor();
  const { publish, subscribe } = useEventBus();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [effortSelection, setEffortSelection] = useState<EffortSelection | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribe(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, () => {
      if (captableInfo?.id) {
        (getCapTableInfo as (id?: number) => Promise<void>)(captableInfo.id);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo?.id, subscribe]);

  const { getDocuments: fetchDocuments, companyDocuments } = useDocuments() as unknown as {
    getDocuments: (mdId: number, companyId?: number) => Promise<void>;
    companyDocuments: CompanyDocuments[];
  };

  useEffect(() => {
    if (companyInfo?.id) {
      fetchDocuments(companyInfo.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo?.id]);

  const documentFileOptions = useMemo(() => {
    const allFiles = (companyDocuments ?? []).flatMap(doc => doc.files || []);
    const seen = new Set<number>();
    return allFiles.filter(f => {
      const id = f.file?.id;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [companyDocuments]);

  const handleOpen = useCallback(() => {
    setSelectedFiles([]);
    setEffortSelection(null);
    setDialogOpen(true);
  }, []);

  const handleConfirm = async () => {
    if (selectedFiles.length === 0 || !companyInfo?.id) return;
    setIsGenerating(true);
    setDialogOpen(false);

    const companyIdStr = companyInfo.id.toString();

    try {
      const newJob = await ApiService.apiCompanyDocumentGenerationJobsNewCreate(companyIdStr, {
        processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
        reference_files: selectedFiles.map(f => f.file_id.toString()) as any,
        desired_termination_state: 'COMPLETE',
        parser_overrides: [
          {
            file_type: 'xlsx',
            xlsx_parsing_mode: 'JSON_CONVERSION',
            minify_json: true,
          } as any,
        ],
        ...buildEffortPayload(effortSelection),
      } as any);

      addJob({
        id: newJob.job_id,
        details: {
          title: 'Generating Cap Table',
          description: `Processing ${selectedFiles.length} document${selectedFiles.length > 1 ? 's' : ''}`,
        },
        pollingConfig: {
          pollingFunction: async () => CompanyService.apiCompanyDocumentGenerationJobsRead(companyIdStr, newJob.job_id),
          pollingInterval: 3000,
          pollingArgs: [],
        },
        onResult: async (result: RetrieveDocumentGenerationJob) => {
          if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
            try {
              const applyResult = await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(companyIdStr, {
                job_id: result.job_id,
              });

              const count = applyResult?.security_ids?.length ?? 0;
              publish(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, result.job_id);
              enqueueSnackbar(
                count > 0
                  ? `Cap table updated with ${count} securities from AI results`
                  : 'Cap table generation completed',
                { variant: 'success' }
              );
            } catch (applyError) {
              Logger.error('GenerateCapTableJob - Error applying generated job', applyError);
              enqueueSnackbar('Job completed but applying results to the cap table failed', {
                variant: 'warning',
              });
            }
            removeJob(result.job_id);
          } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
            enqueueSnackbar('Cap table generation job failed', { variant: 'error' });
            removeJob(result.job_id);
          }
        },
        onError: (error: Error) => {
          Logger.error('GenerateCapTableJob - Generate job polling error', error);
        },
      });

      enqueueSnackbar('Cap table generation started. The cap table will update when the job completes.', {
        variant: 'info',
      });
    } catch (error: any) {
      Logger.error('GenerateCapTableJob - Error creating cap table generation job', error);
      const errorMessage = extractResponseErrorMessage(error) ?? 'Failed to start cap table generation';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setIsGenerating(false);
      setSelectedFiles([]);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
        <AIGradientButton onClick={handleOpen} size="small">
          Generate Cap Table
        </AIGradientButton>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Cap Table from Documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select documents to process. When the job completes, AI-extracted securities will be applied to the cap
            table.
          </Typography>
          <Autocomplete
            multiple
            options={documentFileOptions}
            disableCloseOnSelect
            value={documentFileOptions.filter(opt => selectedFiles.some(s => s.file_id === opt.file?.id))}
            onChange={(_, selected) =>
              setSelectedFiles(
                selected
                  .filter(opt => opt.file?.id != null)
                  .map(opt => ({
                    file_id: opt.file!.id!,
                    label: opt.file?.filename ?? 'Unknown',
                  }))
              )
            }
            getOptionLabel={option => option?.file?.filename ?? 'Unknown file'}
            getOptionKey={option => option.document_reference_id}
            renderOption={(props, option, { selected }) => {
              const { key, ...otherProps } = props;
              return (
                <li key={key} {...otherProps} style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option?.file ? `${option.file.filename}.${option.file.file_type}` : 'Unknown file'}
                </li>
              );
            }}
            renderInput={params => <TextField {...params} variant="outlined" placeholder="Select documents" />}
            isOptionEqualToValue={(option, value) => option.document_reference_id === value.document_reference_id}
          />
          <Box sx={{ mt: 3 }}>
            <EffortSelector firmId={firmInfo?.id ?? null} value={effortSelection} onChange={setEffortSelection} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <AIGradientButton onClick={handleConfirm} disabled={isGenerating || selectedFiles.length === 0}>
            Start Job
          </AIGradientButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateCapTableJob;
